import { useState } from 'react';
import type { FC, MouseEvent } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button } from '@mui/material';
import { graphql, useFragment } from 'react-relay';
import {
    createDownloadPQRUrl,
    createDownloadSummaryPQRUrl,
} from '../../services/url';
import calculatePriceToUnlockResults from '../../services/calculatePriceToUnlockResults';
import { DownloadFileHelper } from '../DownloadFileHelper';
import { CheckMarkIcon } from '../icons';
import theme from '../../theme';
import type { ResultFilter } from '../../schema.graphql';
import ApproveDownloadDialog from './ApproveDownloadDialog';
import type { DownloadPQiSummaryOrQualityReportButton_survey$key } from './__generated__/DownloadPQiSummaryOrQualityReportButton_survey.graphql';

type DownloadPQiSummaryOrQualityReportButtonProps = {
    freeSummary?: boolean;
    survey: DownloadPQiSummaryOrQualityReportButton_survey$key;
    respondentCount?: number;
    filters?: ReadonlyArray<ResultFilter>;
    filterMode?: 'LogicalAnd' | 'LogicalOr';
};

const DownloadPQiSummaryOrQualityReportButton: FC<
    DownloadPQiSummaryOrQualityReportButtonProps
> = (props) => {
    const { freeSummary, respondentCount, filters, filterMode } = props;
    const survey = useFragment(surveyFragment, props.survey);
    const { locale, formatNumber } = useIntl();

    const [showApproveDownloadDialog, setShowApproveDownloadDialog] =
        useState(false);
    const [showDownloadDialog, setShowDownloadDialog] = useState(false);

    // Keeps track of if the user unlocked results, in which case we will show a confirmation message
    const [didUnlockResults, setDidUnlockResults] = useState(false);

    const reportPrice = calculatePriceToUnlockResults(respondentCount);

    const link = freeSummary
        ? createDownloadSummaryPQRUrl(
              survey.projectRawId,
              survey.surveyNumber,
              filters,
              filterMode,
              locale,
          )
        : createDownloadPQRUrl(
              survey.projectRawId,
              survey.surveyNumber,
              filters,
              filterMode,
              locale,
          );
    const handleClickDownload = (event: MouseEvent) => {
        event.preventDefault();
        if (!freeSummary && !survey.resultsUnlocked) {
            setShowApproveDownloadDialog(true);
        } else {
            setShowDownloadDialog(true);
        }
    };

    return (
        <>
            <Button
                fullWidth
                onClick={handleClickDownload}
                variant={'outlined'}
                sx={(theme) => ({
                    backgroundColor: theme.palette.background.paper,
                })}
            >
                {survey.resultsUnlocked ? (
                    <FormattedMessage
                        defaultMessage="Download"
                        description="Download"
                    />
                ) : (
                    `SEK ${formatNumber(reportPrice)}`
                )}
            </Button>
            <ApproveDownloadDialog
                open={showApproveDownloadDialog}
                onClose={(unlockedResults) => {
                    setShowApproveDownloadDialog(false);
                    if (unlockedResults) {
                        setDidUnlockResults(true);
                        setShowDownloadDialog(true);
                    }
                }}
                survey={survey}
            />
            <DownloadFileHelper
                dialogContent={
                    didUnlockResults && (
                        <>
                            <CheckMarkIcon
                                size={48}
                                style={{
                                    background: theme.color.darkGreen,
                                    padding: '14px 10px 10px 14px',
                                    borderRadius: '50%',
                                    color: theme.color.white,
                                }}
                            />
                            <h3>
                                <FormattedMessage defaultMessage="Results unlocked" />
                            </h3>
                        </>
                    )
                }
                noDialog={!didUnlockResults}
                url={link}
                open={showDownloadDialog}
                onClose={() => {
                    setShowDownloadDialog(false);
                    setDidUnlockResults(false);
                }}
            />
        </>
    );
};

const surveyFragment = graphql`
    fragment DownloadPQiSummaryOrQualityReportButton_survey on ProjectSurvey {
        projectRawId
        surveyNumber
        resultsUnlocked
        ...ApproveDownloadDialog_survey
    }
`;

export default DownloadPQiSummaryOrQualityReportButton;
